import * as React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

interface CollapsibleProps {
  children: React.ReactNode;
  className?: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface CollapsibleTriggerProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  isOpen?: boolean;
}

interface CollapsibleContentProps {
  children: React.ReactNode;
  className?: string;
  isOpen?: boolean;
}

const CollapsibleContext = React.createContext<{
  isOpen: boolean;
  toggle: () => void;
} | null>(null);

export const Collapsible: React.FC<CollapsibleProps> = ({ 
  children, 
  className, 
  defaultOpen = false,
  open,
  onOpenChange
}) => {
  const [internalIsOpen, setInternalIsOpen] = React.useState(defaultOpen);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalIsOpen;
  
  const toggle = React.useCallback(() => {
    const newOpen = !isOpen;
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalIsOpen(newOpen);
    }
  }, [isOpen, onOpenChange]);

  return (
    <CollapsibleContext.Provider value={{ isOpen, toggle }}>
      <div className={className}>
        {children}
      </div>
    </CollapsibleContext.Provider>
  );
};

export const CollapsibleTrigger: React.FC<CollapsibleTriggerProps> = ({ 
  children, 
  className,
  onClick
}) => {
  const context = React.useContext(CollapsibleContext);
  
  if (!context) {
    throw new Error("CollapsibleTrigger must be used within a Collapsible");
  }
  
  const handleClick = () => {
    context.toggle();
    onClick?.();
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "flex items-center justify-between w-full text-left",
        className
      )}
    >
      {children}
      <ChevronRight 
        className={cn(
          "h-4 w-4 mr-4 transition-transform duration-200",
          context.isOpen && "rotate-90"
        )} 
      />
    </button>
  );
};

export const CollapsibleContent: React.FC<CollapsibleContentProps> = ({ 
  children, 
  className 
}) => {
  const context = React.useContext(CollapsibleContext);
  
  if (!context) {
    throw new Error("CollapsibleContent must be used within a Collapsible");
  }

  return (
    <div
      className={cn(
        "overflow-hidden transition-all duration-200",
        context.isOpen ? "animate-in slide-in-from-top-2" : "hidden",
        className
      )}
    >
      {children}
    </div>
  );
}; 